import React from 'react';

import InputLabel from 'components/inputfields/InputLabel';
import IProduct from 'models/product';
import { InputTextFieldDebounced } from 'components/inputfields/InputTextField';
import { FloatInputDebounced } from '../FloatInput';
import BooleanField from 'components/inputfields/BooleanField';
import ListDropDown from 'components/inputfields/ListDropDown';
import { ironings } from 'components/constants-ts';
import { IntegerFieldDebounced } from 'components/inputfields/IntegerField';
import ThreeWaySwitch from 'components/inputfields/ThreeWaySwitch';

interface IYarnProps {
  readOnly?: boolean;
  state: IProduct;
  approve?: boolean;
  setState: React.Dispatch<React.SetStateAction<IProduct>>;
}

export const SewingKnittingYarn = ({ readOnly, approve, state, setState }: IYarnProps) => {
  const handleDropdownChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const stateName = event.target.name;
    const stateValue = event.target.value;

    const val = !stateValue || stateValue === '' ? null : stateValue;
    stateName && setState({ ...state, [stateName]: val });
  };

  const styles = { marginBottom: 0, width: 220, flex: 'none' };

  return (
    <>
      <h3>Garnspecifikation</h3>

      <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <InputLabel heading="Vikt & Löplängd" />
          <div style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'center' }}>
            <FloatInputDebounced<IProduct>
              readOnly={readOnly}
              dense={false}
              label="Vikt"
              placeholder="t.ex. 50"
              name="yarnWeight"
              state={state}
              setState={setState}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;gram</h3>

            <h3 style={{ margin: 0 }}>=</h3>

            <FloatInputDebounced<IProduct>
              readOnly={readOnly}
              dense={false}
              label="Löplängd"
              placeholder="t.ex. 120"
              name="yarnRunLength"
              state={state}
              setState={setState}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;meter</h3>
          </div>

          <InputLabel heading="Rekommenderad virknål" />
          <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
            <InputTextFieldDebounced<IProduct>
              readOnly={readOnly}
              label="Rekommenderad virknål"
              placeholder="t.ex. 2,5 eller 4-6"
              inputName="recommendedCrochetHook"
              state={state}
              setState={setState}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;mm</h3>
          </div>

          <InputLabel heading="Rekommenderade stickor" />
          <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
            <InputTextFieldDebounced<IProduct>
              readOnly={readOnly}
              label="Rekommenderade stickor"
              placeholder="t.ex. 9 eller 2,5-3"
              inputName="recommendedKnittingNeedles"
              state={state}
              setState={setState}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;mm</h3>
          </div>

          <InputLabel heading="Stickfasthet" />
          <div style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'center' }}>
            <FloatInputDebounced<IProduct>
              readOnly={readOnly}
              dense={false}
              label="Maskor"
              placeholder="t.ex. 22"
              name="knittingGaugeStitches"
              state={state}
              setState={setState}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;maskor</h3>

            <h3 style={{ margin: 0 }}>x</h3>

            <FloatInputDebounced<IProduct>
              readOnly={readOnly}
              dense={false}
              label="Varv"
              placeholder="t.ex. 30"
              name="knittingGaugeRows"
              state={state}
              setState={setState}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;varv</h3>
          </div>

          <InputLabel heading="Virkfasthet" />
          <div style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'center' }}>
            <FloatInputDebounced<IProduct>
              readOnly={readOnly}
              dense={false}
              label="Maskor"
              placeholder="t.ex. 14"
              name="crochetGaugeStitches"
              state={state}
              setState={setState}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;maskor</h3>

            <h3 style={{ margin: 0 }}>x</h3>

            <FloatInputDebounced<IProduct>
              readOnly={readOnly}
              dense={false}
              label="Varv"
              placeholder="t.ex. 12"
              name="crochetGaugeRows"
              state={state}
              setState={setState}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;varv</h3>
          </div>
        </div>
        {approve && state?.approve?.find(x => x.field === 'yarnSpecs') && (
          <ThreeWaySwitch
            readOnly={readOnly}
            state={state}
            setState={setState}
            name={"yarnSpecs" as keyof IProduct}
          />
        )}
      </div>

      <h3>Skötselanvisningar (textil)</h3>

      <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <InputLabel heading="Tvättemperatur" />
          <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
            <IntegerFieldDebounced<IProduct>
              readOnly={readOnly}
              label="Tvättemperatur"
              placeholder="t.ex. 40"
              inputName="washingTemperature"
              state={state}
              setState={setState}
              style={styles}
            />
            <h3 style={{ margin: 0 }}>&nbsp;°C</h3>
          </div>

          <InputLabel heading="Tvättråd" />
          <InputTextFieldDebounced<IProduct>
            readOnly={readOnly}
            label="Tvättråd"
            placeholder="t.ex. Skontvätt, ej centrifugering"
            inputName="washingInstructions"
            state={state}
            setState={setState}
            style={{ ...styles, width: '100%' }}
          />

          <InputLabel heading="Strykning" />
          <ListDropDown
            readOnly={readOnly}
            disableLabel
            displayEmpty
            listData={ironings}
            value={state.ironing}
            targetName="ironing"
            handleChange={handleDropdownChange}
            style={{ flexGrow: 1, overflow: 'hidden', width: 300 }}
          />

          <BooleanField
            readOnly={readOnly}
            labelOptions={{ heading: 'Torktumling' }}
            name="tumbleDrying"
            state={state}
            setState={setState}
            />
          <BooleanField
            readOnly={readOnly}
            labelOptions={{ heading: 'Kemtvätt' }}
            name="dryCleaning"
            state={state}
            setState={setState}
          />

          <BooleanField
            readOnly={readOnly}
            labelOptions={{ heading: 'Klorblekning' }}
            name="chlorineBleaching"
            state={state}
            setState={setState}
          />
        </div>
        {approve && state?.approve?.find(x => x.field === 'textileCare') && (
          <ThreeWaySwitch
            readOnly={readOnly}
            state={state}
            setState={setState}
            name={"textileCare" as keyof IProduct}
          />
        )}
      </div>
    </>
  );
};

export default SewingKnittingYarn;
